/**
 * ProfileRetrospectives — NC-1281 S4.
 *
 * The second stage of the feedback loop: the Workload-Fazit ("after six runs we
 * see X"), shown ABOVE the individual run retrospectives because it is the
 * step-back statement the reader came for; the per-run observations are the
 * evidence underneath it.
 *
 * Three properties of the data model drive this UI, and each one is easy to
 * "helpfully" break:
 *
 * 1. **Conclusions are append-only and are NOT reconciled.** Several people
 *    contribute independently, and divergent judgements are shown side by side —
 *    that disagreement IS the information. So: no edit, no delete, no "current
 *    verdict" summary that would quietly crown one author's view.
 * 2. **The verdict is never computed from the run verdicts.** A majority of green
 *    runs does not mean a workload has proven itself; that call is a human one.
 *    Nothing here aggregates.
 * 3. **`available: false` is not an empty state.** It means the container could
 *    not be read. Rendering "no feedback yet" for an outage would tell the reader
 *    something false, so the two are kept apart — the same distinction the facts
 *    block already makes for the run history.
 */

/*
 * Test-hook note: `data-testid` lives on `Box`, never on `Stack` or `Card`.
 * `Stack` is not in the curated @mui/material mock and falls through its proxy
 * fallback, which renders a bare <div> and drops every prop — a testid there
 * exists in prod and is invisible to every test, so the assertion could only
 * ever pass. `Box` forwards ...rest.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ConclusionVerdict,
  RunRetrospective,
  WorkloadConclusion,
  WorkloadRetrospectives,
} from '../../../services/types';
import { workloadProfileApi } from '../../../services/workloadProfileService';
import { formatDateSafe } from '../../../utils/dateFormat';
import {
  CONCLUSION_VERDICTS,
  conclusionColor,
  conclusionLabel,
  runRetroColor,
  runRetroLabel,
} from '../../../utils/retrospectiveLabels';

interface Props {
  workloadId: string;
  retrospectives?: WorkloadRetrospectives | null;
  /** operator+ — a viewer reads the feedback but files none. */
  canContribute: boolean;
  /** Re-fetch the profile so a new conclusion appears without a reload. */
  onChanged?: () => void;
}

/** Newest first — a reader wants the current thinking at the top. */
function byNewest<T extends { createdAt: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

const ConclusionEntry: React.FC<{ entry: WorkloadConclusion }> = ({ entry }) => (
  <Box sx={{ py: 1 }} data-testid={`conclusion-${entry.id}`}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <Chip size="small" color={conclusionColor(entry.verdict)} label={conclusionLabel(entry.verdict)} />
      <Typography variant="caption" color="text.secondary">
        {entry.createdByName || entry.createdBy} · {formatDateSafe(entry.createdAt)}
      </Typography>
    </Stack>
    <Typography variant="body2">{entry.statement}</Typography>
    {entry.basedOnRunIds.length > 0 && (
      <Typography variant="caption" color="text.secondary">
        stützt sich auf {entry.basedOnRunIds.length}{' '}
        {entry.basedOnRunIds.length === 1 ? 'Lauf' : 'Läufe'}: {entry.basedOnRunIds.join(', ')}
      </Typography>
    )}
  </Box>
);

const RunRetroEntry: React.FC<{ entry: RunRetrospective }> = ({ entry }) => (
  <Box sx={{ py: 1 }} data-testid={`run-retro-${entry.runId}`}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <Chip size="small" color={runRetroColor(entry.verdict)} label={runRetroLabel(entry.verdict)} />
      <Typography variant="caption" color="text.secondary">
        Lauf {entry.runId} · {entry.createdBy} · {formatDateSafe(entry.createdAt)}
      </Typography>
    </Stack>
    {entry.whatWentWrong && <Typography variant="body2">{entry.whatWentWrong}</Typography>}
    {entry.participantFeedback && (
      <Typography variant="body2" color="text.secondary">
        Teilnehmer: {entry.participantFeedback}
      </Typography>
    )}
    {entry.changeRequested && (
      <Typography variant="body2" color="text.secondary">
        Änderungswunsch: {entry.changeRequested}
      </Typography>
    )}
  </Box>
);

export const ProfileRetrospectives: React.FC<Props> = ({
  workloadId,
  retrospectives,
  canContribute,
  onChanged,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [verdict, setVerdict] = useState<ConclusionVerdict>('bewaehrt');
  const [statement, setStatement] = useState('');
  const [basedOnRaw, setBasedOnRaw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-NC-1281 response: render nothing rather than empty sections that look
  // like a failed load (same rule ProfileFactsAndGaps follows).
  if (!retrospectives) return null;

  const unreadable = retrospectives.available === false;
  const conclusions = byNewest(retrospectives.conclusions ?? []);
  const runRetros = byNewest(retrospectives.runRetros ?? []);

  const submit = async () => {
    if (!canContribute || saving) return;
    if (statement.trim() === '') {
      setError('Ein Fazit braucht eine Aussage.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await workloadProfileApi.postConclusion(workloadId, {
        verdict,
        statement: statement.trim(),
        // Split, trim, drop blanks — an author who types nothing sends [], which
        // the API accepts on purpose ("never used, need gone" is a conclusion).
        basedOnRunIds: basedOnRaw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== ''),
      });
      setStatement('');
      setBasedOnRaw('');
      setVerdict('bewaehrt');
      setFormOpen(false);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fazit konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="profile-retrospectives">
      <Divider sx={{ mb: 2 }} />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">Fazit zu diesem Workload</Typography>
        {canContribute && !unreadable && (
          <Button size="small" onClick={() => setFormOpen((o) => !o)}>
            {formOpen ? 'Abbrechen' : 'Fazit hinzufügen'}
          </Button>
        )}
      </Stack>

      {unreadable ? (
        <Typography variant="body2" data-testid="retrospectives-unavailable">
          Keine Daten — die Rückmeldungen sind derzeit nicht abrufbar.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Mehrere Einschätzungen stehen nebeneinander — unterschiedliche Sichtweisen werden
            bewusst nicht zusammengefasst.
          </Typography>

          {formOpen && (
            <Box data-testid="conclusion-form">
            <Stack spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Einschätzung"
                value={verdict}
                onChange={(e) => setVerdict(e.target.value as ConclusionVerdict)}
              >
                {CONCLUSION_VERDICTS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {conclusionLabel(v)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Aussage"
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
              />

              <TextField
                fullWidth
                size="small"
                label="Stützt sich auf Läufe (optional, RunIds mit Komma getrennt)"
                value={basedOnRaw}
                onChange={(e) => setBasedOnRaw(e.target.value)}
                helperText="Darf leer bleiben — ein Fazit ohne einen einzigen Lauf ist zulässig."
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Box>
                <Button variant="contained" disabled={saving} onClick={() => void submit()}>
                  {saving ? 'Speichert…' : 'Fazit speichern'}
                </Button>
              </Box>
            </Stack>
            </Box>
          )}

          {conclusions.length === 0 ? (
            <Typography variant="body2" data-testid="no-conclusions">
              Noch kein Fazit erfasst.
            </Typography>
          ) : (
            <Box data-testid="conclusion-list">
              <Stack divider={<Divider flexItem />}>
                {conclusions.map((entry) => (
                  <ConclusionEntry key={entry.id} entry={entry} />
                ))}
              </Stack>
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            Rückblicke der einzelnen Läufe
          </Typography>

          {runRetros.length === 0 ? (
            <Typography variant="body2" data-testid="no-run-retros">
              Noch kein Rückblick erfasst.
            </Typography>
          ) : (
            <Box data-testid="run-retro-list">
              <Stack divider={<Divider flexItem />}>
                {runRetros.map((entry) => (
                  <RunRetroEntry key={entry.id} entry={entry} />
                ))}
              </Stack>
            </Box>
          )}

          {retrospectives.ignoredDocs > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: 'block' }}
              data-testid="ignored-docs"
            >
              {retrospectives.ignoredDocs}{' '}
              {retrospectives.ignoredDocs === 1 ? 'Eintrag' : 'Einträge'} in einem hier
              unbekannten Format wurden übersprungen.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ProfileRetrospectives;
